/** Small UI hooks shared by Creations, Studio, Brand Assets and the creative detail modal. */
import { useCallback, useEffect, useRef, useState } from "react";

export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}

/**
 * Modals in this app all listen for Escape on `window`, so a nested dialog (lightbox, confirmation,
 * sub-dialog) would also close the one underneath. Wrap a modal's onClose with this guard and tag the
 * dialog with the returned `marker` class: the close only runs when that dialog is the top-most one.
 */
export function useTopmostClose(onClose: () => void): { marker: string; close: () => void } {
  const marker = useRef(`dlg-${Math.random().toString(36).slice(2, 9)}`).current;
  const close = useCallback(() => {
    const mine = document.querySelector(`.${marker}`);
    const all = Array.from(document.querySelectorAll('[role="dialog"]'));
    const top = all[all.length - 1];
    if (mine && top && top !== mine && !mine.contains(top)) return;
    onClose();
  }, [marker, onClose]);
  return { marker, close };
}

/** Read width/height of a local image or video file (for aspect auto-detection). */
export function readMediaDimensions(file: File): Promise<{ width: number; height: number; durationSec?: number } | null> {
  const url = URL.createObjectURL(file);
  return new Promise((resolve) => {
    const done = (r: { width: number; height: number; durationSec?: number } | null) => {
      URL.revokeObjectURL(url);
      resolve(r);
    };
    if (file.type.startsWith("video/")) {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.onloadedmetadata = () => done(v.videoWidth ? { width: v.videoWidth, height: v.videoHeight, durationSec: Number.isFinite(v.duration) ? v.duration : undefined } : null);
      v.onerror = () => done(null);
      v.src = url;
    } else if (file.type.startsWith("image/")) {
      const img = new Image();
      img.onload = () => done({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => done(null);
      img.src = url;
    } else {
      done(null);
    }
  });
}

const ASPECTS: { label: string; ratio: number }[] = [
  { label: "1:1", ratio: 1 },
  { label: "9:16", ratio: 9 / 16 },
  { label: "4:5", ratio: 4 / 5 },
  { label: "16:9", ratio: 16 / 9 },
];

/** Nearest ad aspect for given pixel dimensions (within 6%), else null. */
export function nearestAspect(width: number, height: number): string | null {
  if (!width || !height) return null;
  const r = width / height;
  let best: { label: string; d: number } | null = null;
  for (const a of ASPECTS) {
    const d = Math.abs(r - a.ratio) / a.ratio;
    if (!best || d < best.d) best = { label: a.label, d };
  }
  return best && best.d <= 0.06 ? best.label : null;
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export function stripExt(name: string): string {
  return name.replace(/\.[a-z0-9]{2,5}$/i, "");
}
